/* eslint-disable max-len */
import Head from 'next/head'
import styled from 'styled-components'

import FooterPageLayout from 'components/FooterPageLayout'
import Spacer from 'components/Spacer'

const Header = styled.h5`
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  text-transform: uppercase;
  margin-bottom: 0;
`

const SmallHeader = styled.h6`
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  margin: 0;
`

const StyledText = styled.p`
  margin: 0;
  padding: 0;
  font-style: normal;
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  text-align: justify;

  color: ${props => props.theme.colors.black};
  opacity: 0.7;

  margin-bottom: 20px;
`

const StyledLink = styled.a`
  text-decoration: none;
  color: ${props => props.theme.colors.purple};
  word-break: break-all;
  &:visited {
    color: ${props => props.theme.colors.purple};
  }
`

const UnderlineLink = styled(StyledLink)`
  text-decoration: underline;
  text-decoration-color: ${props => props.theme.colors.purple};
  text-underline-position: under;
  padding-top: 4px; /* font-correction */
`

const StyledListItem = styled.li`
  margin-bottom: 30px;
`

const OrderedList = styled.div``

const OrderedListItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
`

const OrderedListCircle = styled.div`
  height: 36px;
  width: 36px;
  border-radius: 18px;
  background: ${props => props.theme.colors.yellow};
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2px; /* font correction */
  font-weight: 900;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
`

const Privacy = () => (
  <FooterPageLayout
    imageSrc={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/footer-hero-2.png`}
    title="Privacy Policy"
  >
    <Head>
      <title>Privacy Policy - HomeLister</title>
      <meta
        name="description"
        content="Privacy Policy at HomeLister"
      />
      <meta
        name="keywords"
        content="Privacy Policy, HomeLister"
      />
    </Head>
    <section>
      <StyledText>
        Thank you for choosing to be part of our community at&nbsp;HomeLister, Inc&nbsp;(“Company”,
        “we”, “us”, or “our”). We are committed to protecting your personal information and your
        right to privacy. If you have any questions or concerns about our&nbsp;policy, or our
        practices with regards to your personal information, please contact us at&nbsp;
        <StyledLink href="mailto:info@homelister.com">info@homelister.com.</StyledLink>
      </StyledText>
      <StyledText>
        When you visit our&nbsp;website&nbsp;
        <StyledLink href="https://www.homelister.com">https://www.homelister.com</StyledLink>
        ,&nbsp;and use our services, you trust us with your personal information. We take your
        privacy very seriously. In this&nbsp;privacy policy, we seek to explain to you in the
        clearest way possible what information we collect, how we use it and what rights you have in
        relation to it. We hope you take some time to read through it carefully, as it is important.
        If there are any terms in this&nbsp;privacy policy&nbsp;that you do not agree with, please
        discontinue use of our&nbsp;Sites&nbsp;and our services.
      </StyledText>
      <StyledText>
        This&nbsp;privacy policy&nbsp;applies to all information collected through
        our&nbsp;website&nbsp;(such as&nbsp;
        <StyledLink href="https://www.homelister.com">https://www.homelister.com</StyledLink>
        ),&nbsp;and/or any related services, sales, marketing or events (we refer to them
        collectively in this&nbsp;privacy policy&nbsp;as the &ldquo;Services&rdquo;).
      </StyledText>
      <StyledText>
        <strong>
          Please read this&nbsp;privacy policy&nbsp;carefully as it will help you make informed
          decisions about sharing your personal information with us.
        </strong>
      </StyledText>
    </section>
    <section>
      <Header>TABLE OF CONTENTS</Header>
      <Spacer size={30} />
      <OrderedList>
        <OrderedListItem>
          <OrderedListCircle>1</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter1">WHAT INFORMATION DO WE COLLECT?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>2</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter2">HOW DO WE USE YOUR INFORMATION?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>3</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter3">
            WILL YOUR INFORMATION BE SHARED WITH ANYONE?
          </UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>4</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter4">
            DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
          </UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>5</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter5">DO WE USE GOOGLE MAPS?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>6</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter6">HOW DO WE HANDLE YOUR SOCIAL LOGINS?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>7</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter7">HOW LONG DO WE KEEP YOUR INFORMATION?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>8</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter8">HOW DO WE KEEP YOUR INFORMATION SAFE?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>9</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter9">DO WE COLLECT INFORMATION FROM MINORS?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>10</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter10">WHAT ARE YOUR PRIVACY RIGHTS?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>11</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter11">CONTROLS FOR DO-NOT-TRACK FEATURES</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>12</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter12">
            DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>13</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter13">DO WE MAKE UPDATES TO THIS POLICY?</UnderlineLink>
        </OrderedListItem>
        <OrderedListItem>
          <OrderedListCircle>14</OrderedListCircle>
          <Spacer size={10} />
          <UnderlineLink href="#chapter14">HOW CAN YOU CONTACT US ABOUT THIS POLICY?</UnderlineLink>
        </OrderedListItem>
      </OrderedList>
    </section>
    <section id="chapter1">
      <Header>1. WHAT INFORMATION DO WE COLLECT?</Header>
      <SmallHeader>
        <strong>Personal information you disclose to us</strong>&nbsp;
      </SmallHeader>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We collect personal information that you provide to us.
      </StyledText>
      <StyledText>
        We collect personal information that you voluntarily provide to us when&nbsp;registering at
        the&nbsp;Services expressing an interest in obtaining information about us or our products
        and services, when participating in activities on the&nbsp;Services&nbsp;or otherwise
        contacting us.
      </StyledText>
      <StyledText>
        The personal information that we collect depends on the context of your interactions with us
        and the&nbsp;Services, the choices you make and the products and features you use. The
        personal information we collect can include the following:
      </StyledText>
      <StyledText>
        <strong>Publicly Available Personal Information.</strong>&nbsp;We collect&nbsp;first name,
        maiden name, last name, and nickname;&nbsp;ID;&nbsp;current and former address;&nbsp;phone
        numbers;&nbsp;email addresses;&nbsp;real estate records including purchase and sale prices
        and neighbor info;&nbsp;and other similar data.
      </StyledText>
      <StyledText>
        <strong>Payment Data.</strong>&nbsp;We collect data necessary to process your payment if you
        make purchases, such as your payment instrument number (such as a credit card number), and
        the security code associated with your payment instrument. All payment data is stored
        by&nbsp;Stripe. You may find their privacy policy link(s) here:{' '}
        <StyledLink href="https://stripe.com/privacy">https://stripe.com/privacy.</StyledLink>
      </StyledText>
      <StyledText>
        <strong>Social Media Login Data.</strong>&nbsp;We may provide you with the option to
        register using social media account details, like your Facebook, Twitter or other social
        media account. If you choose to register in this way, we will collect the Information
        described in the section called &ldquo;HOW DO WE HANDLE YOUR SOCIAL LOGINS&rdquo; below.
      </StyledText>
      <StyledText>
        All personal information that you provide to us must be true, complete and accurate, and you
        must notify us of any changes to such personal information.
      </StyledText>
      <SmallHeader>
        <strong>Information automatically collected</strong>&nbsp;
      </SmallHeader>
      <strong>In Short:</strong>&nbsp;Some information — such as IP address and/or browser and
      device characteristics — is collected automatically when you visit our&nbsp;Services.
      <StyledText>
        We automatically collect certain information when you visit, use or navigate
        the&nbsp;Services. This information does not reveal your specific identity (like your name
        or contact information) but may include device and usage information, such as your IP
        address, browser and device characteristics, operating system, language preferences,
        referring URLs, device name, country, location, information about how and when you use
        our&nbsp;Services&nbsp;and other technical information. This information is primarily needed
        to maintain the security and operation of our&nbsp;Services, and for our internal analytics
        and reporting purposes.
      </StyledText>
      <StyledText>
        Like many businesses, we also collect information through cookies and similar technologies.
      </StyledText>
    </section>
    <section id="chapter2">
      <Header>2. HOW DO WE USE YOUR INFORMATION?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We process your information for purposes based on legitimate
        business interests, the fulfillment of our contract with you, compliance with our legal
        obligations, and/or your consent.
      </StyledText>
      <StyledText>
        We use personal information collected via our&nbsp;Services&nbsp;for a variety of business
        purposes described below. We process your personal information for these purposes in
        reliance on our legitimate business interests, in order to enter into or perform a contract
        with you, with your consent, and/or for compliance with our legal obligations. We indicate
        the specific processing grounds we rely on next to each purpose listed below.
      </StyledText>
      <StyledText>
        By providing your information, you consent to us or other third parties contacting you about
        your interest in the Services by email, phone or through any other contact information you
        have chosen to provide. You may opt out of marketing by following the instructions in our
        Privacy Statement.
      </StyledText>
      <StyledText>We use the information we collect or receive:</StyledText>
      <ul>
        <StyledListItem>
          <strong>To facilitate account creation and logon process.</strong>&nbsp;If you choose to
          link your account with us to a third party account (such as your Google or Facebook
          account), we use the information you allowed us to collect from those third parties to
          facilitate account creation and logon process for the performance of the
          contract.&nbsp;See the section below headed &ldquo;HOW DO WE HANDLE YOUR SOCIAL
          LOGINS&rdquo; for further information.
        </StyledListItem>
        <StyledListItem>
          <strong>To send you marketing and promotional communications.</strong>&nbsp;We and/or our
          third party marketing partners may use the personal information you send to us for our
          marketing purposes, if this is in accordance with your marketing preferences. You can
          opt-out of our marketing emails at any time (see the &ldquo;WHAT ARE YOUR PRIVACY
          RIGHTS&rdquo; below).
        </StyledListItem>
        <StyledListItem>
          <strong>To send administrative information to you.</strong>&nbsp;We may use your personal
          information to send you product, service and new feature information and/or information
          about changes to our terms, conditions, and policies.
        </StyledListItem>
        <StyledListItem>
          <strong>Fulfill and manage your orders.</strong>&nbsp;We may use your information to
          fulfill and manage your orders, payments, returns, and exchanges made through
          the&nbsp;Services.
        </StyledListItem>
        <StyledListItem>
          <strong>To post testimonials.</strong>&nbsp;We post testimonials on
          our&nbsp;Services&nbsp;that may contain personal information. Prior to posting a
          testimonial, we will obtain your consent to use your name and testimonial. If you wish to
          update, or delete your testimonial, please contact us at&nbsp;info@homelister.com&nbsp;and
          be sure to include your name, testimonial location, and contact information.
        </StyledListItem>
        <StyledListItem>
          <strong>Deliver targeted advertising to you.</strong>&nbsp;We may use your information to
          develop and display content and advertising (and work with third parties who do so)
          tailored to your interests and/or location and to measure its effectiveness.
        </StyledListItem>
        <StyledListItem>
          <strong>To manage user accounts.</strong>&nbsp;We may use your information for the
          purposes of managing our account and keeping it in working order.
        </StyledListItem>
      </ul>
    </section>
    <section id="chapter3">
      <Header>3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We only share information with your consent, to comply with
        laws, to provide you with services, to protect your rights, or to fulfill business
        obligations.
      </StyledText>
      <StyledText>We may process or share data based on the following legal basis:</StyledText>
      <ul>
        <StyledListItem>
          <strong>Consent:</strong>&nbsp;We may process your data if you have given us specific
          consent to use your personal information in a specific purpose.
        </StyledListItem>
        <StyledListItem>
          <strong>Legitimate Interests:</strong>&nbsp;We may process your data when it is reasonably
          necessary to achieve our legitimate business interests.
        </StyledListItem>
        <StyledListItem>
          <strong>Performance of a Contract:</strong>&nbsp;Where we have entered into a contract
          with you, we may process your personal information to fulfill the terms of our contract.
        </StyledListItem>
        <StyledListItem>
          <strong>Legal Obligations:</strong>&nbsp;We may disclose your information where we are
          legally required to do so in order to comply with applicable law, governmental requests, a
          judicial proceeding, court order, or legal process, such as in response to a court order
          or a subpoena (including in response to public authorities to meet national security or
          law enforcement requirements).
        </StyledListItem>
        <StyledListItem>
          <strong>Vital Interests:</strong>&nbsp;We may disclose your information where we believe
          it is necessary to investigate, prevent, or take action regarding potential violations of
          our policies, suspected fraud, situations involving potential threats to the safety of any
          person and illegal activities, or as evidence in litigation in which we are involved.
        </StyledListItem>
      </ul>
      <StyledText>
        More specifically, we may need to process your data or share your personal information in
        the following situations:
      </StyledText>
      <ul>
        <StyledListItem>
          <strong>Vendors, Consultants and Other Third-Party Service Providers.</strong>&nbsp;We may
          share your data with third party vendors, service providers, contractors or agents who
          perform services for us or on our behalf and require access to such information to do that
          work. Examples include: payment processing, data analysis, email delivery, hosting
          services, customer service and marketing efforts. We may allow selected third parties to
          use tracking technology on the&nbsp;Services, which will enable them to collect data about
          how you interact with the&nbsp;Services&nbsp;over time. This information may be used to,
          among other things, analyze and track data, determine the popularity of certain content
          and better understand online activity. Unless described in this Policy, we do not share,
          sell, rent or trade any of your information with third parties for their promotional
          purposes.
        </StyledListItem>
        <StyledListItem>
          <strong>Business Transfers.</strong>&nbsp;We may share or transfer your information in
          connection with, or during negotiations of, any merger, sale of company assets, financing,
          or acquisition of all or a portion of our business to another company.
        </StyledListItem>
        <StyledListItem>
          <strong>Third-Party Advertisers.</strong>&nbsp;We may use third-party advertising
          companies to serve ads when you visit the&nbsp;Services. These companies may use
          information about your visits to our Website(s) and other websites that are contained in
          web cookies and other tracking technologies in order to provide advertisements about goods
          and services of interest to you.
        </StyledListItem>
      </ul>
    </section>
    <section id="chapter4">
      <Header>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We may use cookies and other tracking technologies to
        collect and store your information.
      </StyledText>
      <StyledText>
        We may use cookies and similar tracking technologies (like web beacons and pixels) to access
        or store information. Specific information about how we use such technologies and how you
        can refuse certain cookies is set out in our Cookie Policy.
      </StyledText>
    </section>
    <section id="chapter5">
      <Header>5. DO WE USE GOOGLE MAPS?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;Yes, we use Google Maps for the purpose of providing better
        service.
      </StyledText>
      <StyledText>
        This website, mobile application, or Facebook application uses Google Maps APIs. You may
        find the Google Maps APIs Terms of Service&nbsp;
        <StyledLink href="https://developers.google.com/maps/terms">here.</StyledLink>To better
        understand Google’s Privacy Policy, please refer to this&nbsp;
        <StyledLink href="https://developers.google.com/maps/terms">link.</StyledLink>
      </StyledText>
      <StyledText>
        By using our Maps API Implementation, you agree to be bound by Google’s Terms of
        Service.&nbsp;We use information about location in conjunction with data from other data
        providers.
      </StyledText>
    </section>
    <section id="chapter6">
      <Header>6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;If you choose to register or log in to our services using a
        social media account, we may have access to certain information about you.
      </StyledText>
      <StyledText>
        Our&nbsp;Services&nbsp;offer you the ability to register and login using your third party
        social media account details (like your Facebook or Twitter logins). Where you choose to do
        this, we will receive certain profile information about you from your social media provider.
        The profile Information we receive may vary depending on the social media provider
        concerned, but will often include your name, e-mail address, friends list, profile picture
        as well as other information you choose to make public.
      </StyledText>
      <StyledText>
        We will use the information we receive only for the purposes that are described in
        this&nbsp;privacy policy&nbsp;or that are otherwise made clear to you on the&nbsp;Services.
        Please note that we do not control, and are not responsible for, other uses of your personal
        information by your third party social media provider. We recommend that you review their
        privacy policy to understand how they collect, use and share your personal information, and
        how you can set your privacy preferences on their sites and apps.
      </StyledText>
    </section>
    <section id="chapter7">
      <Header>7. HOW LONG DO WE KEEP YOUR INFORMATION?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We keep your information for as long as necessary to fulfill
        the purposes outlined in this&nbsp;privacy policy&nbsp;unless otherwise required by law.
      </StyledText>
      <StyledText>
        We will only keep your personal information for as long as it is necessary for the purposes
        set out in this&nbsp;privacy policy, unless a longer retention period is required or
        permitted by law (such as tax, accounting or other legal requirements). No purpose in this
        policy will require us keeping your personal information for longer than&nbsp;the period of
        time in which users have an account with us.
      </StyledText>
      <StyledText>
        When we have no ongoing legitimate business need to process your personal information, we
        will either delete or anonymize it, or, if this is not possible (for example, because your
        personal information has been stored in backup archives), then we will securely store your
        personal information and isolate it from any further processing until deletion is possible.
      </StyledText>
    </section>
    <section id="chapter8">
      <Header>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We aim to protect your personal information through a system
        of organizational and technical security measures.
      </StyledText>
      <StyledText>
        We have implemented appropriate technical and organizational security measures designed to
        protect the security of any personal information we process. However, please also remember
        that we cannot guarantee that the internet itself is 100% secure. Although we will do our
        best to protect your personal information, transmission of personal information to and from
        our&nbsp;Services&nbsp;is at your own risk. You should only access the services within a
        secure environment.
      </StyledText>
    </section>
    <section id="chapter9">
      <Header>9. DO WE COLLECT INFORMATION FROM MINORS?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;We do not knowingly collect data from or market to children
        under 18 years of age.
      </StyledText>
      <StyledText>
        We do not knowingly solicit data from or market to children under 18 years of age. By using
        the&nbsp;Services, you represent that you are at least 18 or that you are the parent or
        guardian of such a minor and consent to such minor dependent’s use of the&nbsp;Services. If
        we learn that personal information from users less than 18 years of age has been collected,
        we will deactivate the account and take reasonable measures to promptly delete such data
        from our records. If you become aware of any data we have collected from children under age
        18, please contact us at&nbsp;__________.
      </StyledText>
    </section>
    <section id="chapter10">
      <Header>10. WHAT ARE YOUR PRIVACY RIGHTS?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;You may review, change, or terminate your account at any
        time.
      </StyledText>
      <StyledText>
        If you have questions or comments about your privacy rights, you may email us at&nbsp;
        <StyledLink href="mailto:info@homelister.com.">info@homelister.com.</StyledLink>
      </StyledText>
      <SmallHeader>
        <strong>Account Information</strong>&nbsp;
      </SmallHeader>
      <StyledText>
        If you would at any time like to review or change the information in your account or
        terminate your account, you can:
      </StyledText>
      <ul>
        <StyledListItem>
          Log into your account settings and update your user account.
        </StyledListItem>
        <StyledListItem>Contact us using the contact information provided.</StyledListItem>
      </ul>
      <StyledText>
        Upon your request to terminate your account, we will deactivate or delete your account and
        information from our active databases. However, some information may be retained in our
        files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our
        Terms of Use and/or comply with legal requirements.
      </StyledText>
      <StyledText>
        <strong>
          <u>Cookies and similar technologies:</u>
        </strong>
        &nbsp;Most Web browsers are set to accept cookies by default. If you prefer, you can usually
        choose to set your browser to remove cookies and to reject cookies. If you choose to remove
        cookies or reject cookies, this could affect certain features or services of
        our&nbsp;Services. To opt-out of interest-based advertising by advertisers on
        our&nbsp;Services&nbsp;visit&nbsp;
        <StyledLink href="http://www.aboutads.info/choices/">
          http://www.aboutads.info/choices/
        </StyledLink>
        .
      </StyledText>
      <StyledText>
        <strong>
          <u>Opting out of email marketing:</u>
        </strong>
        &nbsp;You can unsubscribe from our marketing email list at any time by clicking on the
        unsubscribe link in the emails that we send or by contacting us using the details provided
        below. You will then be removed from the marketing email list – however, we will still need
        to send you service-related emails that are necessary for the administration and use of your
        account. To otherwise opt-out, you may:
      </StyledText>
      <ul>
        <StyledListItem>
          Note your preferences when you register an account with the site.
        </StyledListItem>
        <StyledListItem>Access your account settings and update preferences.</StyledListItem>
        <StyledListItem>Contact us using the contact information provided.</StyledListItem>
      </ul>
    </section>
    <section id="chapter11">
      <Header>11. CONTROLS FOR DO-NOT-TRACK FEATURES</Header>
      <StyledText>
        Most web browsers and some mobile operating systems and mobile applications include a
        Do-Not-Track (“DNT”) feature or setting you can activate to signal your privacy preference
        not to have data about your online browsing activities monitored and collected. No uniform
        technology standard for recognizing and implementing DNT signals has been finalized. As
        such, we do not currently respond to DNT browser signals or any other mechanism that
        automatically communicates your choice not to be tracked online. If a standard for online
        tracking is adopted that we must follow in the future, we will inform you about that
        practice in a revised version of this&nbsp;privacy policy.
      </StyledText>
    </section>
    <section id="chapter12">
      <Header>12. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;Yes, if you are a resident of California, you are granted
        specific rights regarding access to your personal information.
      </StyledText>
      <StyledText>
        California Civil Code Section 1798.83, also known as the “Shine The Light” law, permits our
        users who are California residents to request and obtain from us, once a year and free of
        charge, information about categories of personal information (if any) we disclosed to third
        parties for direct marketing purposes and the names and addresses of all third parties with
        which we shared personal information in the immediately preceding calendar year. If you are
        a California resident and would like to make such a request, please submit your request in
        writing to us using the contact information provided below.
      </StyledText>
      <StyledText>
        If you are under 18 years of age, reside in California, and have a registered account with
        the&nbsp;Services, you have the right to request removal of unwanted data that you publicly
        post on the&nbsp;Services. To request removal of such data, please contact us using the
        contact information provided below, and include the email address associated with your
        account and a statement that you reside in California. We will make sure the data is not
        publicly displayed on the&nbsp;Services, but please be aware that the data may not be
        completely or comprehensively removed from our systems.
      </StyledText>
    </section>
    <section id="chapter13">
      <Header>13. DO WE MAKE UPDATES TO THIS POLICY?</Header>
      <StyledText>
        <strong>In Short:</strong>&nbsp;Yes, we will update this policy as necessary to stay
        compliant with relevant laws.
      </StyledText>
      <StyledText>
        We may update this&nbsp;privacy policy&nbsp;from time to time. The updated version will be
        indicated by an updated “Revised” date and the updated version will be effective as soon as
        it is accessible. If we make material changes to this&nbsp;privacy policy, we may notify you
        either by prominently posting a notice of such changes or by directly sending you a
        notification. We encourage you to review this&nbsp;privacy policy&nbsp;frequently to be
        informed of how we are protecting your information.
      </StyledText>
    </section>
    <section id="chapter14">
      <Header>14. HOW CAN YOU CONTACT US ABOUT THIS POLICY?</Header>
      <StyledText>
        If you have questions or comments about this policy, you may&nbsp;email us
        at&nbsp;info@homelister.com&nbsp;or by post to:
      </StyledText>
      <StyledText>
        HomeLister, Inc
        <br />
        520 Broadway
        <br />
        Santa Monica,&nbsp;CA&nbsp;90401
        <br />
        United States
      </StyledText>
    </section>
    <section>
      <Header>HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</Header>
      <StyledText>
        Based on the laws of some countries, you may have the right to request access to the
        personal information we collect from you, change that information, or delete it in some
        circumstances. To request to review, update, or delete your personal information, please
        submit a request form by clicking&nbsp;
        <StyledLink href="https://app.termly.io/notify/cead8c9c-3fac-4312-a15d-58ab1d3131e9">
          here.
        </StyledLink>
        We will respond to your request within 30 days.
      </StyledText>
    </section>
  </FooterPageLayout>
)

export default Privacy
